import { AuditTimeline } from '@components/ui/AuditTimeline';
import { PERMISSIONS } from '@/permissions';
import { resolveApiError } from '@lib/errors';
import { normalizeBackendIssues } from '@/lib/backend-issues';
import type {
  ChangeHistorySlotProps,
  DescribeErrorContext,
  FormViewSlots,
} from '@resource-kit/ui/react';

/**
 * The hgs wiring for the resource kit's form slots (#2168, ADR-0035 D3).
 *
 * The seam next door (`resourceKitServices.tsx`) translates services the kit
 * asks for; this translates the one thing the kit cannot ask for at all — a
 * business component. `AuditTimeline` reaches into `@/services`, the host's
 * route table and the Shopify actor matcher, so the kit only ever sees the
 * function below.
 *
 * `canView` is the permission decision `ResourceDetailPanel` used to spell
 * inline. It is reproduced here verbatim — admin OR the per-seat grant
 * (#1964) — because the point of the move is that the CATALOGUE
 * (`PERMISSIONS`) is host property, not that the policy changes.
 *
 * A module-level constant, not a hook result: the identity is stable across
 * renders, so the provider next door never re-renders the whole kit tree over
 * a fresh object.
 *
 * The import above reaches the `form/slots` path, which since #2169 PR-3 is a
 * re-export shim over `@resource-kit/ui/react` — the whole react barrel. So
 * this import no longer narrows anything: `test-utils/renderWithProviders.tsx`
 * puts `resourceKitServices` — and therefore this file — into every component
 * suite's module graph, and through this line the entire kit react surface
 * (`FormView`, `FieldSlot`, the relation surface) comes with it. Before PR-3
 * the leaf import kept that out; the narrowing it bought is gone and comes
 * back only when #2170 rewrites this import to a package leaf. The host-side
 * cost is unchanged: this file's own first import puts
 * `@components/ui/AuditTimeline`, and through it `@/services`, `@lib/routes`,
 * `@lib/audit-merge` and `@lib/shopify-actor-match`, into that same graph.
 *
 * `describeError` (#2169 PR-3) is the second slot, and it arrived for a
 * different reason than the first. `FormView` used to compose it inline; when
 * `FormView` moved into `@resource-kit/ui` the composition could not follow,
 * because BOTH halves are host facts. `normalizeBackendIssues` reads
 * `ApiError`, this app's HTTP client's error class, off a real 400/422 body;
 * `resolveApiError` resolves a backend sentinel against this app's catalogs
 * under the resource's own namespace, falling back to `error.unknown`. The body
 * below is the pre-move expression, moved rather than rewritten.
 *
 * `normalizeBackendIssues` comes from the form's `validation` LEAF, which is
 * still host code (it is not a shim), so that import does stay narrow — and
 * it is the one member of that file the controllers package did not take:
 * everything else there is framework-free algebra, while this reads a host
 * class.
 */
export const hgsFormViewSlots: FormViewSlots = {
  changeHistory: {
    canView: (access) => access.isAdmin || access.hasGrant(PERMISSIONS.AUDIT_READ),
    render: <T,>(props: ChangeHistorySlotProps<T>) => <AuditTimeline<T> {...props} />,
  },
  describeError: <T,>(error: unknown, ctx: DescribeErrorContext) => ({
    issues: normalizeBackendIssues<T>(error),
    // A GETTER, not a value (#2179). `useFormView` reads exactly one half per
    // failure — issues when there are any, `message` otherwise — so `message`
    // is resolved only when read. A response carrying both would make that
    // resolution redundant, but no backend producer emits one; it's headroom.
    //
    // The seam type stays `{ message: string; issues: Issue<T>[] }`: an
    // accessor satisfies it, `toEqual` reads it like any own enumerable
    // property, and nothing downstream changes. The branch ORDER in
    // `useFormView` is untouched — only WHEN `message` is computed moves.
    get message() {
      return resolveApiError(
        error,
        { t: ctx.t, exists: ctx.exists },
        { namespace: ctx.namespace, fallbackKey: 'error.unknown' },
      );
    },
  }),
};
